// TelcoTrack — finance srovnání: kategorie prací a materiálu dle PPD (Tabulka_PPD.xlsx)

/** Skupiny prací — odpovídají listům výpisu práce + PPD rekapitulaci. */
export const FINANCE_WORK_BUCKET_IDS = [
  'stavba',
  'montaz',
  'zafuk',
  'optika',
  'priprava_ostatni',
] as const;

export type FinanceWorkBucketId = (typeof FINANCE_WORK_BUCKET_IDS)[number];

export const FINANCE_WORK_BUCKET_LABELS: Record<FinanceWorkBucketId, string> = {
  stavba: 'Stavba',
  montaz: 'Montáž',
  zafuk: 'Záfuk',
  optika: 'Sváry / optika',
  priprava_ostatni: 'Příprava a ostatní',
};

/** Skupiny materiálu dle sekcí PPD (Limitka materiálu). */
export const FINANCE_MATERIAL_BUCKET_IDS = [
  'material_vyrazeny',
  'material_zhotovitel_vykazovany',
  'material_zhotovitel_nevykazovany',
  'material_cetin',
  'material_ostatni',
] as const;

export type FinanceMaterialBucketId = (typeof FINANCE_MATERIAL_BUCKET_IDS)[number];

export const FINANCE_MATERIAL_BUCKET_LABELS: Record<FinanceMaterialBucketId, string> = {
  material_vyrazeny: 'Materiál vyřazený',
  material_zhotovitel_vykazovany: 'Materiál zhotovitele — vykazovaný',
  material_zhotovitel_nevykazovany: 'Materiál zhotovitele — nevykazovaný',
  material_cetin: 'Materiál dod. CETIN',
  material_ostatni: 'Materiál ostatní',
};

export interface FinanceComparisonBucketRow {
  id: string;
  label: string;
  kind: 'work' | 'material';
  planned: number;
  actual: number;
  variance: number;
}

function normalizePpdCategoryText(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeWorkName(value: string | null | undefined): string {
  return normalizePpdCategoryText(value);
}

function normalizeFinanceQuantityUnit(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/\^2/g, '2')
    .replace(/\^3/g, '3');
}

/** Jen lineární metry — ne m², ks, hod, t, … */
export function isFinanceMeterUnit(unit: string | null | undefined): boolean {
  return normalizeFinanceQuantityUnit(unit) === 'm';
}

/** Montáž multiduct (952582) — montážní bucket, ne stavba / výkop. */
export const FINANCE_MONTAZ_MULTIDUCT_SAP = '952582';

/**
 * Průvrt závrty — kanonický finance klíč (ceník výkazu Montáž závrty → 954944).
 * PPD varianty do 300 / 300–600 / nad 600 mm se aliasují sem.
 */
export const FINANCE_MONTAZ_ZAVRT_SAP = '954944';

/** PPD průvrt smíšený tl. (všechny tloušťky) → závrty. */
export const FINANCE_MONTAZ_ZAVRT_PPD_SAP_CODES = ['954942', '954943', '954944'] as const;

/** Pokládka HDPE / multiduct (952602) — ve finance tabulce pod Stavba → Pokládka (mimo Výkop). */
export const FINANCE_POKLADKA_HDPE_MULTIDUCT_SAP = '952602';

/** Pokládka PE / vrapované chráničky (954970) — Stavba → Pokládka. */
export const FINANCE_POKLADKA_PE_VRAPOVANE_SAP = '954970';

/** Instalace trubičky v budově / digitální cesta (958302) — montáž. */
export const FINANCE_INSTALACE_TRUBICKY_SAP = '958302';

/** Podvrt neřízený (do 150 mm) — kanonický klíč; 952380 (nad 150) se aliasuje sem. */
export const FINANCE_PODVRT_NERIDENY_SAP = '954968';

/** Podvrt řízený (do 150 mm) — kanonický klíč; 952384 (nad 150) se aliasuje sem. */
export const FINANCE_PODVRT_RIDENY_SAP = '954969';

export function isMontazMultiductWorkName(name: string | null | undefined): boolean {
  const n = normalizeWorkName(name);
  if (!n) return false;
  if (n.includes('pokladka') && n.includes('hdpe')) return false;
  if (n.includes('montaz multiduct')) return true;
  if ((n.includes('multiduct') || n.includes('multikanal')) && n.includes('montaz')) return true;
  return false;
}

/** PPD řádek závrtů (954942–954944) — Montáž závrty / průvrt zdivem, ne zemní práce. */
export function isMontazZavrtPpdLine(sap?: string | null, name?: string | null): boolean {
  const sapKey = sap?.trim();
  if (
    sapKey &&
    (FINANCE_MONTAZ_ZAVRT_PPD_SAP_CODES as readonly string[]).includes(sapKey)
  ) {
    return true;
  }
  const n = normalizeWorkName(name);
  return n.includes('pruvrt zdivem smisenym');
}

/** Pokládka HDPE / multiduct (výkaz i PPD názvy). */
export function isPokladkaHdpeMultiductWorkName(name: string | null | undefined): boolean {
  const n = normalizeWorkName(name);
  if (!n) return false;
  if (n.includes('pokladka') && (n.includes('hdpe') || n.includes('multiduct'))) return true;
  if (n.includes('pokladka') && n.includes('multikanal')) return true;
  return false;
}

/** Pokládka PE / vrapované chráničky (PPD 954970). */
export function isPokladkaPeVrapovaneWorkName(name: string | null | undefined): boolean {
  const n = normalizeWorkName(name);
  if (!n || !n.includes('pokladka')) return false;
  return n.includes('vrapov') || (n.includes(' pe ') || n.endsWith(' pe') || n.includes('pe nebo'));
}

/** Instalace trubičky / digitální cesta (PPD 958302). */
export function isInstalaceTrubickyOrDigitalniCestaWorkName(
  name: string | null | undefined,
): boolean {
  const n = normalizeWorkName(name);
  if (!n) return false;
  if (n.includes('instalace trubicky')) return true;
  if (n.includes('instalace') && n.includes('digitalni')) return true;
  if (n.includes('montaz digitalni cesta')) return true;
  return false;
}

export function isFinancePodvrtNeridenySap(sap?: string | null): boolean {
  const s = sap?.trim();
  return s === FINANCE_PODVRT_NERIDENY_SAP || s === '952380';
}

export function isFinancePodvrtRidenySap(sap?: string | null): boolean {
  const s = sap?.trim();
  return s === FINANCE_PODVRT_RIDENY_SAP || s === '952384';
}

export function isFinancePodvrtWorkName(name: string | null | undefined): boolean {
  const n = normalizeWorkName(name);
  if (!n) return false;
  // UI „Povrch …“ i PPD „Podvrt …“
  return n.includes('podvrt') || (n.includes('povrch') && (n.includes('rizen') || n.includes('nerizen')));
}

/**
 * Stavba → Pokládka — pokládka HDPE/multiduct (952602) + PE/vrapované (954970), mimo čistý výkop.
 * Montáž multiduct (952582) sem nepatří (bucket montáž).
 */
export function isFinanceStavbaPokladkaLine(
  activityName: string | null | undefined,
  sap?: string | null,
): boolean {
  const sapKey = sap?.trim();
  if (sapKey === FINANCE_MONTAZ_MULTIDUCT_SAP || isMontazMultiductWorkName(activityName)) {
    return false;
  }
  if (sapKey === FINANCE_POKLADKA_HDPE_MULTIDUCT_SAP) return true;
  if (sapKey === FINANCE_POKLADKA_PE_VRAPOVANE_SAP) return true;
  if (isPokladkaPeVrapovaneWorkName(activityName)) return true;
  return isPokladkaHdpeMultiductWorkName(activityName);
}

/** @deprecated Alias — používejte isFinanceStavbaPokladkaLine. */
export const isFinanceStavbaMultiductLine = isFinanceStavbaPokladkaLine;

/** Vyloučené názvy — obnova, pokládka, nad rámec, přesun, bourání, … (→ Stavba / Obnova, Ostatní nebo Pokládka). */
function isFinanceStavbaVykopExcludedName(name: string): boolean {
  return (
    name.includes('obnova') ||
    name.includes('pokladka') ||
    name.includes('nad ramec') ||
    name.includes('presun') ||
    name.includes('ostranen') ||
    name.includes('bourani') ||
    name.includes('rozvoz') ||
    name.includes('pujcovna dek') ||
    name.includes('hodinova sazba')
  );
}

/** Čistá excavační práce — výkop / rýha / prohloubení / podvrt (normalizovaný název). */
function isFinancePureExcavationName(name: string): boolean {
  return (
    name.includes('vykop') ||
    name.includes('ryha') ||
    name.includes('prohlouben') ||
    name.includes('podvrt') ||
    (name.includes('povrch') && (name.includes('rizen') || name.includes('nerizen')))
  );
}

/**
 * Řádky stavby pro podkategorii Výkop — jen čistý výkop / rýha / prohloubení / podvrt v metrech.
 * Vyloučeno: pokládka HDPE/multiduct/PE (→ Pokládka), obnova, nad rámec, přesun, bourání, rozvoz, jednotky ≠ m.
 */
export function isFinanceStavbaVykopLine(
  activityName: string | null | undefined,
  sap?: string | null,
  unit?: string | null,
): boolean {
  const sapKey = sap?.trim();
  if (sapKey === FINANCE_MONTAZ_MULTIDUCT_SAP) return false;
  if (isFinanceStavbaPokladkaLine(activityName, sap)) return false;
  if (isFinancePodvrtNeridenySap(sapKey) || isFinancePodvrtRidenySap(sapKey)) {
    return isFinanceMeterUnit(unit);
  }

  if (!isFinanceMeterUnit(unit)) return false;

  const name = normalizeWorkName(activityName);
  if (!name) return false;
  if (isMontazMultiductWorkName(name)) return false;
  if (isFinanceStavbaVykopExcludedName(name)) return false;

  return isFinancePureExcavationName(name);
}

/**
 * Obnova povrchu — jen položky s „obnova“ v názvu (vč. „Obnova nad rámec …“, „Obnova betonu …“).
 * Nad rámec bez obnovy, ostranění, přesun, bourání, … → Stavba / Ostatní.
 */
export function isFinanceStavbaObnovaLine(activityName: string | null | undefined): boolean {
  const name = normalizeWorkName(activityName);
  if (!name) return false;
  return name.includes('obnova');
}

/**
 * Zbylé stavební položky mimo výkop, pokládku a obnovu povrchu.
 * Přesun materiálu, hodinová sazba, bourání, nad rámec (bez obnovy), rozvoz, …
 */
export function isFinanceStavbaOstatniLine(activityName: string | null | undefined): boolean {
  const name = normalizeWorkName(activityName);
  if (!name) return false;
  if (isFinanceStavbaObnovaLine(name)) return false;
  return (
    name.includes('nad ramec') ||
    name.includes('presun') ||
    name.includes('ostranen') ||
    name.includes('bourani') ||
    name.includes('rozvoz') ||
    name.includes('pujcovna dek') ||
    name.includes('hodinova sazba') ||
    (name.includes('zakopan') && name.includes('prace'))
  );
}

export function isZafukWorkName(name: string | null | undefined): boolean {
  const n = normalizeWorkName(name);
  return n.includes('zafuk') || n.includes('vyfuk') || n.includes('prefuk');
}

function isOptikaWorkName(name: string): boolean {
  const n = normalizeWorkName(name);
  return (
    n.includes('svar') ||
    n.includes('otdr') ||
    n.includes('mereni') ||
    n.includes('optick') ||
    n.includes('vlakn')
  );
}

function normalizeRoleToken(role: string | null | undefined): string {
  return normalizePpdCategoryText(role).replace(/á/g, 'a').replace(/é/g, 'e').replace(/í/g, 'i');
}

/** PPD řádek práce → finance bucket (kategorie z exportu + název položky). */
export function resolvePpdWorkFinanceBucket(
  category: string | null | undefined,
  name?: string | null,
  sap?: string | null,
): FinanceWorkBucketId {
  const cat = normalizePpdCategoryText(category);
  const itemName = normalizeWorkName(name);
  const sapKey = sap?.trim();

  if (sapKey === FINANCE_MONTAZ_MULTIDUCT_SAP || isMontazMultiductWorkName(itemName)) {
    return 'montaz';
  }

  if (isMontazZavrtPpdLine(sapKey, itemName)) {
    return 'montaz';
  }

  if (
    sapKey === FINANCE_INSTALACE_TRUBICKY_SAP ||
    isInstalaceTrubickyOrDigitalniCestaWorkName(itemName)
  ) {
    return 'montaz';
  }

  if (cat.includes('zemni prace') || cat.includes('provozni prace')) {
    return 'stavba';
  }

  if (cat.includes('montaz') || cat.includes('montáž')) {
    if (isZafukWorkName(itemName)) return 'zafuk';
    if (isOptikaWorkName(itemName)) return 'optika';
    return 'montaz';
  }

  if (isZafukWorkName(itemName)) return 'zafuk';
  if (isOptikaWorkName(itemName)) return 'optika';

  return 'priprava_ostatni';
}

/** PPD kategorie materiálu → finance bucket. */
export function resolvePpdMaterialFinanceBucket(category: string | null | undefined): FinanceMaterialBucketId {
  const cat = normalizePpdCategoryText(category);
  if (!cat || !cat.includes('material')) return 'material_ostatni';
  if (cat.includes('vyrazen')) return 'material_vyrazeny';
  if (cat.includes('dod') && cat.includes('cetin')) return 'material_cetin';
  if (cat.includes('nevykazovan')) return 'material_zhotovitel_nevykazovany';
  if (cat.includes('vykazovan')) return 'material_zhotovitel_vykazovany';
  if (cat.includes('zhotovitel')) return 'material_zhotovitel_vykazovany';
  return 'material_ostatni';
}

/** Výkaz práce → stejný bucket jako plán (role z ceníku, fallback název). */
export function resolveWorkReportFinanceBucket(
  activityName: string | null | undefined,
  role?: string | null,
): FinanceWorkBucketId {
  const roleNorm = normalizeRoleToken(role);
  if (roleNorm === 'stavba') return 'stavba';
  if (roleNorm === 'montaz') return 'montaz';
  if (roleNorm === 'zafuk') return 'zafuk';
  if (roleNorm === 'optika') return 'optika';

  const name = normalizeWorkName(activityName);
  if (!name) return 'priprava_ostatni';

  if (isMontazMultiductWorkName(name)) return 'montaz';
  if (isMontazZavrtPpdLine(undefined, activityName)) return 'montaz';
  if (isInstalaceTrubickyOrDigitalniCestaWorkName(activityName)) return 'montaz';

  if (
    name.includes('vykop') ||
    name.includes('ryha') ||
    name.includes('pokladka hdpe') ||
    name.includes('pokladka pe') ||
    name.includes('vrapov') ||
    name.includes('podvrt') ||
    name.includes('bourani') ||
    name.includes('rozvoz') ||
    name.includes('obnova') ||
    name.includes('ostraneni') ||
    name.includes('pujcovna dek') ||
    name.includes('hodinova sazba')
  ) {
    return 'stavba';
  }

  if (isZafukWorkName(name)) return 'zafuk';
  if (isOptikaWorkName(name) || name.includes('svar')) return 'optika';

  if (
    name.includes('montaz') ||
    name.includes('vystavba') ||
    name.includes('dokumentace') ||
    name.includes('listovani') ||
    name.includes('rozvadec') ||
    name.includes('matrix') ||
    name.includes('zavrt') ||
    name.includes('digitalni cesta') ||
    name.includes('instalace trubicky')
  ) {
    return 'montaz';
  }

  return 'priprava_ostatni';
}

export function emptyFinanceWorkBucketTotals(): Record<FinanceWorkBucketId, number> {
  return {
    stavba: 0,
    montaz: 0,
    zafuk: 0,
    optika: 0,
    priprava_ostatni: 0,
  };
}

export function emptyFinanceMaterialBucketTotals(): Record<FinanceMaterialBucketId, number> {
  return {
    material_vyrazeny: 0,
    material_zhotovitel_vykazovany: 0,
    material_zhotovitel_nevykazovany: 0,
    material_cetin: 0,
    material_ostatni: 0,
  };
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

export function addToBucketTotals<T extends string>(
  totals: Record<T, number>,
  bucket: T,
  amount: number,
): void {
  if (!Number.isFinite(amount)) return;
  totals[bucket] = roundMoney((totals[bucket] ?? 0) + amount);
}

/** Součet PPD produktových bucketů — bez rekapitulace / JV z hlavičky importu. */
export function plannedFinanceTotalsFromBuckets(
  plannedWork: Record<FinanceWorkBucketId, number>,
  plannedMaterial: Record<FinanceMaterialBucketId, number>,
): { plannedWorkRevenue: number; plannedMaterialCost: number; plannedTotal: number } {
  const plannedWorkRevenue = roundMoney(
    FINANCE_WORK_BUCKET_IDS.reduce((sum, id) => sum + (plannedWork[id] ?? 0), 0),
  );
  const plannedMaterialCost = roundMoney(
    FINANCE_MATERIAL_BUCKET_IDS.reduce((sum, id) => sum + (plannedMaterial[id] ?? 0), 0),
  );
  return {
    plannedWorkRevenue,
    plannedMaterialCost,
    plannedTotal: roundMoney(plannedWorkRevenue + plannedMaterialCost),
  };
}

export function buildFinanceComparisonBucketRows(
  plannedWork: Record<FinanceWorkBucketId, number>,
  actualWork: Record<FinanceWorkBucketId, number>,
  plannedMaterial: Record<FinanceMaterialBucketId, number>,
  actualMaterial: Record<FinanceMaterialBucketId, number>,
): FinanceComparisonBucketRow[] {
  const rows: FinanceComparisonBucketRow[] = [];

  for (const id of FINANCE_WORK_BUCKET_IDS) {
    const planned = plannedWork[id] ?? 0;
    const actual = actualWork[id] ?? 0;
    if (planned === 0 && actual === 0) continue;
    rows.push({
      id: `work-${id}`,
      label: `Práce — ${FINANCE_WORK_BUCKET_LABELS[id]}`,
      kind: 'work',
      planned,
      actual,
      variance: roundMoney(planned - actual),
    });
  }

  for (const id of FINANCE_MATERIAL_BUCKET_IDS) {
    const planned = plannedMaterial[id] ?? 0;
    const actual = actualMaterial[id] ?? 0;
    if (planned === 0 && actual === 0) continue;
    rows.push({
      id: `material-${id}`,
      label: FINANCE_MATERIAL_BUCKET_LABELS[id],
      kind: 'material',
      planned,
      actual,
      variance: roundMoney(planned - actual),
    });
  }

  return rows;
}
